import { useDefaultStore } from '@/data/Content/DefaultStore';
import {
	fetcher,
	getProduct,
	getProductByKeyType,
	getProductSWRKey,
} from '@/data/Content/Product-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { PRODUCT_DATA_KEY } from '@/data/Content/_Product';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { expand, shrink } from '@/data/utils/keyUtil';
import { currencyFallbackMiddleWare } from '@/data/utils/swr/currencyFallbackMiddleWare';
import { isBuyableProduct } from 'presentation/events/utils/productUtils';
import { useMemo } from 'react';
import useSWR from 'swr';
export { getProduct, getProductByKeyType };

const DATA_KEY = PRODUCT_DATA_KEY;

// Maybe need to revisit and use id instead for consistency of cache and fallback
type Props = {
	id?: string;
	isCEId?: boolean;
	condition?: boolean;
	contractId?: string | string[];
	profileName?: string;
};

const validation = (storeId: string, id: string, condition: boolean, physicalStoreId: string) =>
	storeId && id && condition && physicalStoreId;
export const useProduct = ({
	id = '',
	isCEId = false,
	condition = true,
	contractId,
	profileName = SearchProfiles.HDM_FIND_PRODUCT_BY_PART_NUMBER_DETAILS,
}: Props) => {
	const params = useExtraRequestParameters();
	const router = useNextRouter();
	const { settings } = useSettings();
	const { user } = useUser();
	const currentContract = getContractIdParamFromContext(user?.context);
	const contract = contractId ? { contractId } : currentContract;
	const {
		storeId,
		defaultCatalogId: catalogId,
		langId,
		defaultCurrency,
	} = getClientSideCommon(settings, router);
	const { physicalStoreId, uniqueId: stLocId } = useDefaultStore();
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const lookupKey = isCEId ? 'id' : 'partNumber';
	const cacheKey = getProductSWRKey({
		storeId,
		lookupKey,
		lookupValue: id,
		catalogId,
		langId,
		physicalStoreId,
		profileName,
		marketId,
		stLocId,
		...contract,
		...getCurrencyParamFromContext(user?.context),
	});

	const { data, error, isLoading, isValidating } = useSWR(
		validation(storeId, id, condition, physicalStoreId) ? [shrink(cacheKey), DATA_KEY] : null,
		async ([props]) => fetcher(true)(expand(props), params),
		{ use: [currencyFallbackMiddleWare({ defaultCurrency })] }
	);
	const product = useMemo(
		() =>
			data?.product &&
			isBuyableProduct({
				productAttributes: data.product?.attributes ?? [],
				physicalStoreId,
			})
				? data?.product
				: undefined,
		[data, physicalStoreId]
	);
	return {
		rawData: data,
		product: data?.product ?? undefined,
		buyableProduct: product,
		loading: !!id && !error && isLoading && condition,
		isValidating, // This is used to determine whether to show the spinner when the product is being fetched
		error,
	};
};
